import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MapperMap } from "./MapperMap";

interface GpsAccuracyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Initial coordinates the modal opens at (e.g. the PIL's current
   *  position). The modal will then attempt to seed a fresh GPS reading. */
  initialLat: number;
  initialLng: number;
  ordinal?: number;
  /** Called once the user taps "Confirm". The lat/lng passed are read from
   *  `map.getCenter()` at confirm-time, NOT the device's GPS reading. */
  onConfirm: (lat: number, lng: number) => void;
  /** Optional flag to show a saving spinner on the confirm button. */
  isSaving?: boolean;
  /** Live device position rendered as the blue dot on the map. */
  userPos?: { lat: number; lng: number; accuracy?: number } | null;
}

/**
 * Two-step GPS accuracy flow used by PILFormSheet:
 *   1. Tap "Update GPS" — we call getCurrentPosition() to seed the map.
 *   2. The modal opens with a satellite map + a fixed crosshair pin in the
 *      centre. The user pans the map under the pin, and "Confirm" saves
 *      `map.getCenter()` to the server. The crosshair stays put visually;
 *      the map slides beneath it.
 */
export function GpsAccuracyModal({
  open,
  onOpenChange,
  initialLat,
  initialLng,
  ordinal,
  onConfirm,
  isSaving,
  userPos,
}: GpsAccuracyModalProps) {
  const [center, setCenter] = useState<{ lat: number; lng: number }>({
    lat: initialLat,
    lng: initialLng,
  });

  // The map mounts with `defaultCenter`; further centre updates happen via
  // user pans (reported through onCenterChanged). We only want to seed
  // `defaultCenter` once per modal opening — this ref captures it.
  const seedRef = useRef<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    if (!open) {
      seedRef.current = null;
      return;
    }
    // Seed with whatever the parent passed in. The parent is responsible
    // for calling getCurrentPosition() before opening so the seed is fresh.
    seedRef.current = { lat: initialLat, lng: initialLng };
    setCenter({ lat: initialLat, lng: initialLng });
  }, [open, initialLat, initialLng]);

  const handleCenterChanged = useCallback((lat: number, lng: number) => {
    setCenter({ lat, lng });
  }, []);

  const handleConfirm = useCallback(() => {
    if (!Number.isFinite(center.lat) || !Number.isFinite(center.lng)) {
      toast.error("Map centre is not ready yet — try panning the map.");
      return;
    }
    onConfirm(center.lat, center.lng);
  }, [center, onConfirm]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[90dvh] max-h-[90dvh] w-full max-w-full flex-col gap-0 p-0 sm:max-w-2xl"
        showCloseButton={false}
      >
        <DialogHeader className="border-b px-4 py-3">
          <DialogTitle className="text-base">
            {ordinal != null ? `Place spot ${ordinal}` : "Place installation spot"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Pan the map so the red pin sits exactly on the mounting point.
            Pinch to zoom in for accuracy.
          </DialogDescription>
        </DialogHeader>

        <div className="relative flex-1 overflow-hidden">
          {seedRef.current && (
            <MapperMap
              className="absolute inset-0"
              defaultCenter={seedRef.current}
              defaultZoom={20}
              centerCrosshair
              onCenterChanged={handleCenterChanged}
              userPos={userPos ?? null}
            />
          )}
        </div>

        <div className="border-t bg-background/95 backdrop-blur px-4 py-3 space-y-2">
          <p className="text-[11px] text-muted-foreground tabular-nums">
            <MapPin className="mr-1 inline-block h-3 w-3 align-[-2px]" />
            {center.lat.toFixed(6)}, {center.lng.toFixed(6)}
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 min-h-12"
              size="lg"
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={isSaving}
              className="flex-1 min-h-12 gap-2"
              size="lg"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                "Confirm"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
